import { App, Button, Dropdown } from 'antd';
import {
  DownOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import { useState, type ReactNode } from 'react';
import { downloadFile } from '@/shared/lib/download';
import { extractError } from '@/shared/lib/error-message';

type Kind = 'xlsx' | 'pdf' | 'csv';

export interface ExportItem {
  /** Discriminator for the icon + default label. */
  kind: Kind;
  /** Override the auto-derived label (default: "Export Excel"/"Export PDF"/"Export CSV"). */
  label?: string;
  /** Server endpoint (relative, e.g. `/reports/exports/daybook.xlsx`). Mutually exclusive with `onExport`. */
  url?: string;
  /** Query params for the server endpoint. */
  params?: Record<string, unknown>;
  /** Filename for the downloaded file (server endpoint only). */
  fileName?: string;
  /** Custom export handler — runs entirely client-side. Mutually exclusive with `url`. */
  onExport?: () => void | Promise<void>;
  /** Greyed-out + tooltip when nothing to export. */
  disabled?: boolean;
}

interface Props {
  items: ExportItem[];
  /** Override the trigger label. Default: "Export" (or the single item's label when there's only one). */
  label?: ReactNode;
  /** Show the primary item's icon on the trigger. Defaults to true when there's a single item. */
  inlineIcon?: boolean;
  type?: 'default' | 'primary' | 'text';
  size?: 'small' | 'middle' | 'large';
  disabled?: boolean;
}

/**
 * Unified export trigger.
 *
 * - One item: renders a plain Button that fires that item.
 * - Multiple items: renders a dropdown with one entry per format.
 *
 * Server-side exports go through {@link downloadFile} so the JWT travels in
 * the Authorization header (not the URL). Errors surface via `App.useApp().message`.
 */
export function ExportButton({
  items,
  label,
  inlineIcon,
  type = 'default',
  size = 'middle',
  disabled,
}: Props) {
  const { message } = App.useApp();
  const [busy, setBusy] = useState<string | null>(null);

  const runItem = async (item: ExportItem) => {
    const key = keyFor(item);
    setBusy(key);
    try {
      if (item.url) {
        await downloadFile(
          item.url,
          item.params ?? {},
          item.fileName ?? defaultFileName(item),
        );
      } else if (item.onExport) {
        await item.onExport();
      }
    } catch (e) {
      message.error(extractError(e, 'Export failed'));
    } finally {
      setBusy(null);
    }
  };

  if (items.length === 0) return null;

  if (items.length === 1) {
    const only = items[0];
    return (
      <Button
        type={type}
        size={size}
        icon={inlineIcon === false ? undefined : iconFor(only.kind)}
        disabled={disabled || only.disabled}
        loading={busy === keyFor(only)}
        onClick={() => runItem(only)}
      >
        {label ?? only.label ?? defaultLabel(only.kind)}
      </Button>
    );
  }

  return (
    <Dropdown
      disabled={disabled}
      menu={{
        items: items.map((item) => ({
          key: keyFor(item),
          icon: iconFor(item.kind),
          label: item.label ?? defaultLabel(item.kind),
          disabled: item.disabled,
          onClick: () => runItem(item),
        })),
      }}
    >
      <Button type={type} size={size} loading={busy != null}>
        {label ?? 'Export'} <DownOutlined style={{ fontSize: 10 }} />
      </Button>
    </Dropdown>
  );
}

function iconFor(kind: Kind) {
  switch (kind) {
    case 'xlsx': return <FileExcelOutlined />;
    case 'pdf':  return <FilePdfOutlined />;
    case 'csv':  return <FileTextOutlined />;
  }
}

function defaultLabel(kind: Kind) {
  switch (kind) {
    case 'xlsx': return 'Export Excel';
    case 'pdf':  return 'Export PDF';
    case 'csv':  return 'Export CSV';
  }
}

function defaultFileName(item: ExportItem) {
  if (!item.url) return `export.${item.kind}`;
  const last = item.url.split('/').pop() ?? `export.${item.kind}`;
  return last.includes('.') ? last : `${last}.${item.kind}`;
}

function keyFor(item: ExportItem) {
  return `${item.kind}:${item.url ?? item.label ?? 'custom'}`;
}
